import { useEffect, useRef } from 'react';
import type { FC, RefObject } from 'react';
import { Room, RoomEvent, Track } from 'livekit-client';
import type { RemoteTrack, RemoteTrackPublication } from 'livekit-client';
import { fetchPublicViewerToken } from '@/api/teleopApi';
import { logError, logInfo, logWarn } from '@/lib/logger';

const DEFAULT_TRACK = 'robot_overhead';
const TRACK_ORDER = ['robot_overhead', 'robot_perspective'];
const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 10000;

type PublicViewerStatus = 'idle' | 'connecting' | 'connected' | 'failed';

type LiveKitPublicViewerProviderProps = {
  robotId: string;
  roomName: string;
  videoRef: RefObject<HTMLVideoElement>;
  onStatusChange: (status: PublicViewerStatus) => void;
};

const getTrackName = (publication: RemoteTrackPublication) => {
  const publicationTrackName = publication.trackName;

  if (publicationTrackName) {
    return publicationTrackName;
  }

  const legacyPublication = publication as { name?: string };
  const legacyTrackName = legacyPublication.name || '';
  return legacyTrackName;
};

const sortTrackNames = (trackNames: string[]) => {
  const uniqueTrackNames = [...new Set(trackNames)];
  const sortedTrackNames = uniqueTrackNames.sort((first, second) => {
    const firstIndex = TRACK_ORDER.indexOf(first);
    const secondIndex = TRACK_ORDER.indexOf(second);

    if (firstIndex === -1 && secondIndex === -1) {
      return first.localeCompare(second);
    }

    if (firstIndex === -1) {
      return 1;
    }

    if (secondIndex === -1) {
      return -1;
    }

    return firstIndex - secondIndex;
  });
  return sortedTrackNames;
};

const collectVideoTrackNames = (room: Room) => {
  const trackNames: string[] = [];

  room.remoteParticipants.forEach((participant) => {
    participant.trackPublications.forEach((publication) => {
      if (publication.kind !== Track.Kind.Video) {
        return;
      }

      const trackName = getTrackName(publication);

      if (!trackName) {
        return;
      }

      trackNames.push(trackName);
    });
  });

  const sortedTrackNames = sortTrackNames(trackNames);
  return sortedTrackNames;
};

const prepareVideoElement = (video: HTMLVideoElement) => {
  video.autoplay = true;
  video.muted = true;
  video.playsInline = true;
  video.setAttribute('playsinline', 'true');
  video.setAttribute('webkit-playsinline', 'true');
};

const playVideo = (video: HTMLVideoElement) => {
  const playPromise = video.play();

  if (!playPromise) {
    return;
  }

  playPromise.catch((error) => {
    logWarn('public_preview.video.play_failed', 'Public preview video play failed', {
      error: String(error),
      ready_state: video.readyState,
      paused: video.paused,
    });
  });
};

const LiveKitPublicViewerProvider: FC<LiveKitPublicViewerProviderProps> = ({
  robotId,
  roomName,
  videoRef,
  onStatusChange,
}) => {
  const activeTrackRef = useRef<RemoteTrack | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptRef = useRef(0);

  useEffect(() => {
    if (!robotId || !roomName) {
      return;
    }

    let canceled = false;
    let connectInFlight = false;
    const room = new Room();

    const clearReconnectTimeout = () => {
      if (!reconnectTimeoutRef.current) {
        return;
      }

      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    };

    const detachActiveTrack = () => {
      const activeTrack = activeTrackRef.current;
      const video = videoRef.current;

      if (activeTrack && video) {
        activeTrack.detach(video);
      }

      activeTrackRef.current = null;
    };

    const scheduleReconnect = () => {
      if (canceled || reconnectTimeoutRef.current) {
        return;
      }

      const delayMs = Math.min(
        RECONNECT_BASE_DELAY_MS * 2 ** reconnectAttemptRef.current,
        RECONNECT_MAX_DELAY_MS
      );
      reconnectAttemptRef.current += 1;
      reconnectTimeoutRef.current = setTimeout(() => {
        reconnectTimeoutRef.current = null;
        void connect();
      }, delayMs);
    };

    const attachPreferredTrack = () => {
      const trackNames = collectVideoTrackNames(room);
      const preferredTrackName = trackNames.includes(DEFAULT_TRACK)
        ? DEFAULT_TRACK
        : trackNames[0];

      if (!preferredTrackName) {
        return;
      }

      room.remoteParticipants.forEach((participant) => {
        participant.trackPublications.forEach((publication) => {
          if (publication.kind !== Track.Kind.Video) {
            return;
          }

          const trackName = getTrackName(publication);
          const shouldSubscribe = trackName === preferredTrackName;
          publication.setSubscribed(shouldSubscribe);
        });
      });
    };

    const handleTrackSubscribed = (track: RemoteTrack, publication: RemoteTrackPublication) => {
      if (track.kind !== Track.Kind.Video) {
        return;
      }

      const trackName = getTrackName(publication);
      const isDefaultTrack = trackName === DEFAULT_TRACK;
      const hasActiveTrack = activeTrackRef.current !== null;

      if (hasActiveTrack && !isDefaultTrack) {
        return;
      }

      const video = videoRef.current;

      if (!video) {
        return;
      }

      detachActiveTrack();
      activeTrackRef.current = track;
      track.attach(video);
      prepareVideoElement(video);
      playVideo(video);
    };

    const handleDisconnected = () => {
      detachActiveTrack();
      onStatusChange('failed');
      scheduleReconnect();
    };

    const connect = async () => {
      if (connectInFlight || canceled) {
        return;
      }

      connectInFlight = true;
      onStatusChange('connecting');

      try {
        const tokenResponse = await fetchPublicViewerToken(robotId, roomName);

        if (canceled) {
          return;
        }

        await room.connect(tokenResponse.url, tokenResponse.token, {
          autoSubscribe: false,
        });

        if (canceled) {
          return;
        }

        reconnectAttemptRef.current = 0;
        clearReconnectTimeout();
        attachPreferredTrack();
        onStatusChange('connected');
        logInfo('public_preview.livekit.connected', 'Public preview connected', {
          room: roomName,
        });
      } catch (error) {
        if (canceled) {
          return;
        }

        logError('public_preview.livekit.connect_failed', 'Public preview connect failed', {
          error: String(error),
          room: roomName,
        });
        onStatusChange('failed');
        scheduleReconnect();
      } finally {
        connectInFlight = false;
      }
    };

    room.on(RoomEvent.TrackSubscribed, handleTrackSubscribed);
    room.on(RoomEvent.TrackPublished, attachPreferredTrack);
    room.on(RoomEvent.ParticipantConnected, attachPreferredTrack);
    room.on(RoomEvent.Disconnected, handleDisconnected);

    void connect();

    return () => {
      canceled = true;
      detachActiveTrack();
      clearReconnectTimeout();
      room.off(RoomEvent.TrackSubscribed, handleTrackSubscribed);
      room.off(RoomEvent.TrackPublished, attachPreferredTrack);
      room.off(RoomEvent.ParticipantConnected, attachPreferredTrack);
      room.off(RoomEvent.Disconnected, handleDisconnected);
      room.disconnect();
      onStatusChange('idle');
    };
  }, [onStatusChange, robotId, roomName, videoRef]);

  return null;
};

export default LiveKitPublicViewerProvider;
